import { api } from '../../services/api'

/**
 * Cliente del agendamiento en línea (`/api/public/booking`). Todo lo que hay
 * antes de `reserve` es público y sin identidad: catálogo y horas. Los tipos
 * espejan lo que devuelve el backend — nada de paciente salvo el nombre de
 * pila en la confirmación.
 */

export interface TriageChip {
  key: string
  label: string
  hint: string
  serviceId: string | null
}

/** Los tres flujos del paso 1: consultas, exámenes, procedimientos (DT-61). */
export type BookingFlow = 'consultas' | 'examenes' | 'procedimientos'

export interface BookableService {
  id: string
  code: string
  name: string
  description?: string | null
  category: string
  modality: 'IN_PERSON' | 'HOME_VISIT' | 'TELEMEDICINE'
  durationMin: number
  /** Código del arancel Fonasa, cuando la clínica ya lo cargó. */
  fonasaCode?: string | null
  schedulingMode: 'PROFESSIONAL' | 'RESOURCE'
  requiresMedicalOrder: boolean
  flow: BookingFlow
  /** Vacío en los exámenes: la hora la manda la sala, no un profesional. */
  professionalIds: string[]
}

/** Previsión del catálogo (DT-60): el paso 3 ofrece estas y nada más. */
export interface PublicInsuranceProvider {
  id: string
  code: string
  name: string
  kind: 'FONASA' | 'ISAPRE' | 'PARTICULAR'
}

export interface PublicProfessional {
  id: string
  firstName: string
  lastName: string
  specialty?: string | null
  healthRole: string
}

export interface BookingCatalog {
  triage: TriageChip[]
  flows: Array<{ key: BookingFlow; label: string; hint: string }>
  services: BookableService[]
  professionals: PublicProfessional[]
  locations: Array<{ id: string; name: string; address?: string | null }>
  insuranceProviders: PublicInsuranceProvider[]
}

export interface PublicSlot {
  startsAt: string
  endsAt: string
  professionalId: string
  locationId: string
  boxId: string | null
  /** Cupo de la segunda tanda de un día copado (DT-62). */
  isOverbooking: boolean
}

export interface ReserveBody {
  serviceId: string
  professionalId: string
  startsAt: string
  documentType: 'RUT' | 'PASSPORT'
  document: string
  firstName: string
  lastName: string
  email: string
  phone: string
  /** Previsión elegida del catálogo (DT-60). */
  insuranceProviderId?: string | null
  triage?: string | null
  notes?: string | null
  visitAddress?: string | null
  consent: true
}

export interface ReservedAppointment {
  id: string
  startsAt: string
  endsAt: string
  status: string
  modality: string
  isOverbooking: boolean
  service: { name: string; durationMin: number }
  professional: { firstName: string; lastName: string; specialty?: string | null }
  location: { name: string; address?: string | null }
}

export interface ReserveResult {
  appointment: ReservedAppointment
  patientFirstName: string
  sentTo: string | null
  token: string
  alreadyExisted: boolean
}

/** Lo que el enlace de autoservicio muestra y permite (DT-57). */
export interface SelfServiceView {
  status: string
  startsAt: string
  endsAt: string
  modality: string
  isOverbooking: boolean
  visitAddress?: string | null
  videoCallUrl?: string | null
  service: { name: string; durationMin: number }
  professional: { firstName: string; lastName: string; specialty?: string | null }
  location: { name: string; address?: string | null }
  patientFirstName: string
  cancellationWindowHours: number
  canConfirm: boolean
  canCancel: boolean
  lateCancellation: boolean
}

export async function fetchCatalog(): Promise<BookingCatalog> {
  return (await api.get<BookingCatalog>('/public/booking/catalog')).data
}

export async function fetchAvailability(serviceId: string, professionalId: string | undefined, from: Date, days = 7) {
  return (
    await api.get<PublicSlot[]>('/public/booking/availability', {
      params: { serviceId, ...(professionalId ? { professionalId } : {}), from: from.toISOString(), days },
    })
  ).data
}

export async function reserve(body: ReserveBody): Promise<ReserveResult> {
  return (await api.post<ReserveResult>('/public/booking/reserve', body)).data
}

export async function fetchSelfService(token: string): Promise<SelfServiceView> {
  return (await api.get<SelfServiceView>(`/public/booking/appointment/${encodeURIComponent(token)}`)).data
}

export async function confirmByToken(token: string): Promise<SelfServiceView> {
  return (await api.post<SelfServiceView>(`/public/booking/appointment/${encodeURIComponent(token)}/confirm`)).data
}

export async function cancelByToken(token: string): Promise<SelfServiceView> {
  return (await api.post<SelfServiceView>(`/public/booking/appointment/${encodeURIComponent(token)}/cancel`)).data
}
